import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieConsentContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Note: Les cookies Stripe sont considérés comme "nécessaires" car ils sont indispensables
// pour le traitement sécurisé des paiements et la prévention de la fraude.
// Selon le RGPD, ils peuvent être exemptés de consentement explicite.

const COOKIE_CONSENT_KEY = 'cookie-consent-preferences';
const COOKIE_CONSENT_VERSION = '1.0'; // Incrémentez si vous changez la politique

export const CookieConsentProvider = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Toujours accepté (fonctionnement du site)
    analytics: false,
    marketing: false,
    version: COOKIE_CONSENT_VERSION
  });
  const [hasLoaded, setHasLoaded] = useState(false);

  // Charger les préférences au démarrage
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        
        // Vérifier si la version correspond
        if (parsed.version === COOKIE_CONSENT_VERSION) {
          setPreferences(parsed);
          setShowBanner(false);
        } else {
          // Nouvelle version de la politique → redemander le consentement
          setShowBanner(true);
        }
      } else {
        // Première visite → afficher le banner
        setShowBanner(true);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des préférences cookies:', error);
      setShowBanner(true);
    }
    setHasLoaded(true);
  }, []);

  // Sauvegarder les préférences
  const savePreferences = (newPreferences) => {
    const preferencesToSave = {
      ...newPreferences,
      necessary: true, // Toujours true
      version: COOKIE_CONSENT_VERSION
    };
    
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferencesToSave));
      setPreferences(preferencesToSave);
      setShowBanner(false);
      
      // Notifier le changement pour les services externes
      window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', {
        detail: preferencesToSave
      }));
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences cookies:', error);
    }
  };

  // Accepter tous les cookies
  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  // Refuser les cookies optionnels
  const rejectOptional = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  // Ouvrir les paramètres
  const openSettings = () => {
    setShowBanner(true);
  };

  // Réinitialiser les préférences
  const resetPreferences = () => {
    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      setPreferences({
        necessary: true,
        analytics: false,
        marketing: false,
        version: COOKIE_CONSENT_VERSION
      });
      setShowBanner(true);
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des préférences:', error);
    }
  };

  // Vérifier si un type de cookie est autorisé
  const isAllowed = (type) => {
    return preferences[type] === true;
  };

  const value = {
    // État
    showBanner,
    preferences,
    hasLoaded,
    
    // Actions
    acceptAll,
    rejectOptional,
    savePreferences,
    openSettings,
    resetPreferences,
    setShowBanner,
    
    // Utilitaires
    isAllowed
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
